import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Scale,
  Clock,
  FileText,
  FolderEdit,
  ArrowRight,
  CheckCircle,
  Shield,
  ClipboardCheck,
  Stamp,
  Link2,
  Database,
  Sparkles
} from 'lucide-react';
import { Carousel, CarouselContent, CarouselItem } from '../components/ui/carousel';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const resolveModeImage = (baseName) => {
  return `/images/${baseName}-dark.svg`;
};

const LandingPage = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [timelineSummary, setTimelineSummary] = useState({
    eventsCount: null,
    documentsCount: null,
    hasData: false,
  });
  const [carouselApi, setCarouselApi] = useState(null);
  const featureSectionRef = useRef(null);
  const [releaseProgress, setReleaseProgress] = useState(0);

  useEffect(() => {
    if (authLoading || !isAuthenticated) return undefined;

    let canceled = false;
    const fetchLatestTimelineSummary = async () => {
      try {
        const response = await api.get('/changelog', {
          params: {
            tool: 'timeline',
            limit: 1,
            sort_order: 'desc',
          },
        });

        const latest = response.data?.entries?.[0];
        const details = latest?.details || {};
        const eventsCount = Number.isFinite(details.events_count) ? details.events_count : null;
        const documentsCount = Array.isArray(details.files_processed)
          ? details.files_processed.length
          : Number.isFinite(details.files_processed)
            ? details.files_processed
            : null;

        if (!canceled && eventsCount !== null && documentsCount !== null) {
          setTimelineSummary({
            eventsCount,
            documentsCount,
            hasData: true,
          });
        }
      } catch (error) {
        if (!canceled) {
          console.debug('Could not load timeline summary from changelog', error);
        }
      }
    };

    void fetchLatestTimelineSummary();
    return () => {
      canceled = true;
    };
  }, [authLoading, isAuthenticated]);

  useEffect(() => {
    if (!carouselApi) return undefined;
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const timerId = window.setInterval(() => {
      if (!document.hidden) {
        carouselApi.scrollNext();
      }
    }, 4200);

    return () => {
      window.clearInterval(timerId);
    };
  }, [carouselApi]);

  const modes = [
    {
      icon: Clock,
      title: 'Timeline Generator',
      description: 'Extract event chronology, dependencies, and issue clusters from large discovery folders.',
      imageBase: 'timeline-generator',
      path: '/tools/timeline',
      modeLabel: 'Chronology Engine',
      detail: 'Date normalization + issue tagging',
      accent: 'bg-sky-500/15 text-sky-500 border-sky-500/35',
      datapoint: 'Outputs sortable event timelines with legal context.',
      tint: 'hsla(203, 89%, 58%, 0.24)',
    },
    {
      icon: FolderEdit,
      title: 'Smart File Renamer',
      description: 'Batch normalize file naming conventions with deterministic patterns and AI-assisted metadata.',
      imageBase: 'file-renamer',
      path: '/tools/renamer',
      modeLabel: 'Naming Engine',
      detail: 'Pattern templates + job controls',
      accent: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/35',
      datapoint: 'Renames at scale with pause/resume and retries.',
      tint: 'hsla(153, 75%, 44%, 0.22)',
    },
    {
      icon: FileText,
      title: 'Discovery Response Generator',
      description: 'Generate structured draft responses with attorney-profile controls and reusable defaults.',
      imageBase: 'discovery-response-generator',
      path: '/tools/discovery',
      modeLabel: 'Draft Engine',
      detail: 'Template-aware pleadings output',
      accent: 'bg-amber-500/15 text-amber-500 border-amber-500/35',
      datapoint: 'Formats responses for RFAs, RFPs, FROGs, and SIs.',
      tint: 'hsla(41, 90%, 56%, 0.24)',
    },
    {
      icon: ClipboardCheck,
      title: 'QC Runner',
      description: 'High-volume Bates extraction and duplicate checks with report generation and resolution flow.',
      imageBase: 'qc-runner',
      path: '/tools/qc-runner',
      modeLabel: 'Quality Engine',
      detail: 'Bates pattern + duplicate workflows',
      accent: 'bg-violet-500/15 text-violet-500 border-violet-500/35',
      datapoint: 'Produces audit-friendly spreadsheet outputs.',
      tint: 'hsla(262, 87%, 64%, 0.22)',
    },
    {
      icon: Stamp,
      title: 'Exhibit Builder',
      description: 'Create numbered exhibits from Dropbox folders, stamp PDFs, and generate an Exhibit Log Excel file.',
      imageBase: 'all-in-one-runner',
      path: '/tools/exhibit',
      modeLabel: 'Exhibit Engine',
      detail: 'Stamping + exhibit log output',
      accent: 'bg-indigo-500/15 text-indigo-500 border-indigo-500/35',
      datapoint: 'Assigns exhibit numbers with page-level stamp control.',
      tint: 'hsla(232, 84%, 63%, 0.24)',
    },
    {
      icon: Link2,
      title: 'Family Binder',
      description: 'Merge parent-child document families into unified PDFs using DAT/CSV/XLSX relationship files.',
      imageBase: 'family-binder',
      path: '/tools/family-binder',
      modeLabel: 'Family Engine',
      detail: 'Relationship-driven merge workflow',
      accent: 'bg-cyan-500/15 text-cyan-500 border-cyan-500/35',
      datapoint: 'Creates bundle-ready family artifacts by linkage.',
      tint: 'hsla(190, 91%, 55%, 0.24)',
    },
  ];

  const benefits = [
    { icon: CheckCircle, text: 'Deterministic outputs for legal review' },
    { icon: Shield, text: 'Secure Dropbox-integrated workflow' },
    { icon: Database, text: 'Data-first processing with traceability' },
  ];

  const scrollNarrative = [
    {
      title: 'Ingest',
      text: 'Connect one source of truth and keep every document flowing through a repeatable pipeline.',
    },
    {
      title: 'Normalize',
      text: 'Use deterministic rules first, then controlled AI fallback where judgment actually adds value.',
    },
    {
      title: 'Deliver',
      text: 'Produce review-ready outputs with auditable artifacts and consistent legal formatting.',
    },
  ];

  const metricTiles = useMemo(() => [
    { label: 'Tool Modes', value: '6 Active' },
    { label: 'Core Pipelines', value: 'Timeline / Rename / QC' },
    {
      label: 'Latest Timeline Pull',
      value: timelineSummary.hasData
        ? `${timelineSummary.eventsCount} events · ${timelineSummary.documentsCount} docs`
        : 'Waiting for first run'
    },
    { label: 'Operating Model', value: 'Legal Ops + Data QA' },
  ], [timelineSummary]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const updateReleaseProgress = () => {
      const featuresEl = featureSectionRef.current;
      if (!featuresEl) return;
      const top = featuresEl.getBoundingClientRect().top;
      const start = window.innerHeight * 0.9;
      const end = window.innerHeight * 0.45;
      const nextProgress = Math.min(1, Math.max(0, (start - top) / Math.max(1, start - end)));
      setReleaseProgress(nextProgress);
    };

    updateReleaseProgress();
    window.addEventListener('scroll', updateReleaseProgress, { passive: true });
    window.addEventListener('resize', updateReleaseProgress);
    return () => {
      window.removeEventListener('scroll', updateReleaseProgress);
      window.removeEventListener('resize', updateReleaseProgress);
    };
  }, []);

  const carouselReleaseStyle = useMemo(() => {
    const opacity = Math.max(0, 1 - (releaseProgress * 0.88));
    const translateY = -20 * releaseProgress;
    const scale = 1 - (releaseProgress * 0.03);
    return {
      opacity,
      transform: `translate3d(0, ${translateY}px, 0) scale(${scale})`,
    };
  }, [releaseProgress]);

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Hero Section */}
      <section
        className="relative overflow-visible py-14 lg:py-20 landing-sticky-stage"
        data-testid="hero-section"
      >
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="landing-sticky-grid">
            <div className="order-2 xl:order-1 space-y-6 min-w-0">
              <div className="space-y-6 opacity-0 animate-fade-in-up">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/12 text-primary text-sm font-medium">
                  <Scale className="w-4 h-4" />
                  <span>Legal Intelligence Command Center</span>
                </div>

                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold tracking-tight leading-tight">
                  One Surface for <br />
                  <span className="text-primary">Document Operations</span>
                </h1>

                <p className="text-lg text-foreground/82 dark:text-foreground/90 max-w-xl">
                  PKEC Tools combines legal workflow automation with data-heavy execution layers, from chronology extraction to QC and response drafting.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/tools">
                    <Button size="lg" className="w-full sm:w-auto gap-2" data-testid="get-started-btn">
                      Open Command Center
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                  <Link to="/settings">
                    <Button variant="outline" size="lg" className="w-full sm:w-auto" data-testid="configure-btn">
                      Configure Integrations
                    </Button>
                  </Link>
                </div>

                <div className="grid sm:grid-cols-2 gap-3 pt-2">
                  {metricTiles.map((tile, idx) => (
                    <Card
                      key={tile.label}
                      className="p-3 opacity-0 animate-fade-in-up"
                      style={{ animationDelay: `${idx * 90 + 160}ms` }}
                    >
                      <p className="text-[11px] uppercase tracking-[0.16em] text-foreground/72 dark:text-foreground/84 mb-1">{tile.label}</p>
                      <p className="text-sm font-medium">{tile.value}</p>
                    </Card>
                  ))}
                </div>

                <div className="flex flex-wrap gap-5 pt-1">
                  {benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-foreground/82 dark:text-foreground/90">
                      <benefit.icon className="w-4 h-4 text-primary" />
                      <span>{benefit.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-4 pt-4 pb-40 xl:pb-72">
                {scrollNarrative.map((item, idx) => (
                  <Card key={item.title} className="p-4">
                    <span className="text-[11px] uppercase tracking-[0.18em] text-foreground/72 dark:text-foreground/84">Phase {idx + 1}</span>
                    <h3 className="text-xl font-serif font-semibold mt-1 mb-2">{item.title}</h3>
                    <p className="text-sm text-foreground/80 dark:text-foreground/88">{item.text}</p>
                  </Card>
                ))}
              </div>
            </div>

            <div
              className={`order-1 xl:order-2 min-w-0 landing-carousel-stick-wrap opacity-0 animate-fade-in-up animate-delay-200 ${
                releaseProgress > 0.08 ? 'is-releasing' : ''
              } ${releaseProgress > 0.95 ? 'is-released' : ''}`}
              style={carouselReleaseStyle}
            >
              <Card className="landing-carousel-shell overflow-hidden p-3 sm:p-4 mx-auto">
                <div className="relative overflow-hidden rounded-[1rem]">
                  <Carousel
                    setApi={setCarouselApi}
                    opts={{ loop: true, align: 'start', watchDrag: false }}
                    className="w-full max-w-full overflow-hidden rounded-[1rem] select-none"
                  >
                    <CarouselContent className="-ml-0" style={{ marginLeft: 0 }}>
                      {modes.map((mode, modeIndex) => (
                        <CarouselItem key={mode.path} className="pl-0" style={{ paddingLeft: 0 }}>
                          <div className="flex flex-col gap-4">
                            <div className="mode-visual-frame mode-visual-frame--hero" style={{ '--mode-tint': mode.tint }}>
                              <img
                                src={resolveModeImage(mode.imageBase)}
                                alt={mode.title}
                                className="mode-visual-image"
                                loading={modeIndex === 0 ? 'eager' : 'lazy'}
                                decoding="async"
                              />
                              <div className="absolute left-4 right-4 bottom-4">
                                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-primary/35 bg-primary/15 text-primary text-xs">
                                  <Sparkles className="w-3.5 h-3.5" />
                                  <span>{mode.modeLabel}</span>
                                </div>
                              </div>
                            </div>

                            <Card className="rounded-2xl p-4">
                              <div className="space-y-3">
                                <div className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs border ${mode.accent}`}>
                                  <mode.icon className="w-3.5 h-3.5" />
                                  <span>{mode.detail}</span>
                                </div>
                                <h3 className="text-2xl font-serif font-semibold leading-tight">{mode.title}</h3>
                                <p className="text-sm text-foreground/80 dark:text-foreground/88">{mode.description}</p>
                                <Link to={mode.path} className="inline-flex items-center text-primary text-sm font-medium pt-2">
                                  <span>Launch Mode</span>
                                  <ArrowRight className="w-4 h-4 ml-2" />
                                </Link>
                              </div>
                            </Card>
                          </div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                  </Carousel>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featureSectionRef} className="py-20" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14 space-y-4">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold">
              Workflow Mode Matrix
            </h2>
            <p className="text-foreground/80 dark:text-foreground/88 max-w-2xl mx-auto">
              Designed for legal teams that think in data pipelines: ingest, normalize, verify, and deliver.
            </p>
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
            {modes.map((mode, idx) => (
              <Link
                key={mode.path}
                to={mode.path}
                className="block group h-full opacity-0 animate-fade-in-up"
                style={{ animationDelay: `${idx * 90 + 200}ms` }}
                data-testid={`feature-card-link-${idx}`}
              >
                <Card
                  className="feature-hover-card h-full overflow-hidden"
                  data-testid={`feature-card-${idx}`}
                >
                  <div className="mode-thumb m-3 mb-1 min-h-[188px]" style={{ '--mode-tint': mode.tint }}>
                    <img
                      src={resolveModeImage(mode.imageBase)}
                      alt={`${mode.title} preview`}
                      className="mode-visual-image"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <CardHeader className="px-5 pt-3 pb-1 space-y-1">
                    <div className="flex items-center justify-between">
                      <div className={`w-12 h-12 rounded-xl border ${mode.accent} flex items-center justify-center`}>
                        <mode.icon className="w-5 h-5" />
                      </div>
                      <span className="text-[11px] uppercase tracking-[0.18em] text-foreground/72 dark:text-foreground/84">
                        Mode {String(idx + 1).padStart(2, '0')}
                      </span>
                    </div>
                    <CardTitle className="font-serif text-xl group-hover:text-primary transition-colors">
                      {mode.title}
                    </CardTitle>
                    <CardDescription className="text-sm leading-snug">
                      {mode.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="px-5 pb-4 pt-0">
                    <p className="text-xs text-foreground/78 dark:text-foreground/86 mb-2">{mode.datapoint}</p>
                    <div className="flex items-center text-primary text-sm font-medium">
                      <span>Launch Mode</span>
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/tools">
              <Button size="lg" variant="outline" className="gap-2" data-testid="explore-tools-btn">
                Explore All Tools
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Card className="p-8 sm:p-12">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold mb-4">
              Ready to Run the Stack?
            </h2>
            <p className="text-foreground/80 dark:text-foreground/88 mb-8 max-w-2xl mx-auto">
              Connect Dropbox and activate the full legal-data workflow from one interface.
            </p>
            <Link to="/settings">
              <Button size="lg" className="gap-2" data-testid="connect-dropbox-btn">
                Connect Dropbox
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-border" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Scale className="w-5 h-5 text-primary" />
              <span className="font-serif font-semibold">PKEC Tools</span>
            </div>
            <p className="text-sm text-foreground/78 dark:text-foreground/86">
              © {new Date().getFullYear()} PKEC Tools. Built for legal professionals.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
